import { useId, useState } from 'react';
import type { User } from '@/domain';
import { useSetUserPassword } from '@/application/usecases/useAdmin';
import { useConfirmAction } from '@/features/shell/useConfirmAction';
import { failureWords } from '@/shared/failure/failure';
import { strings } from '@/shared/strings';
import { Button } from '@/ui/primitives/Button';
import { Modal, modalStyles } from '@/ui/primitives/Modal';
import { toast } from '@/ui/primitives/toastStore';
import styles from './UsersScreen.module.css';
import { PasswordInput } from '@/ui/primitives/PasswordInput';

/**
 * A manager sets somebody else's password: they have forgotten it, or it has
 * been shared. The server signs them out on every device, so a password that
 * has leaked stops working the moment it is replaced.
 *
 * Your own password is not set here. It goes through Change password, which
 * asks for the current one, and the team list offers no button on your row.
 *
 * Two passwords that agree are asked about before they are sent, the request
 * of 5 October 2026 (third), section 2: the question says the person is signed
 * out everywhere, and Cancel comes back to the form as it was.
 */
export function SetPasswordModal({ user, onClose }: { user: User; onClose: () => void }) {
  const save = useSetUserPassword();
  const ask = useConfirmAction();
  const passwordId = useId();
  const repeatId = useId();
  const [password, setPassword] = useState('');
  const [repeat, setRepeat] = useState('');
  const [errors, setErrors] = useState<Record<string, string[]>>({});

  function submit() {
    const local: Record<string, string[]> = {};
    if (password.length < 8) local.newPassword = [strings.users.passwordTooShort];
    else if (repeat !== password) local.repeat = [strings.users.passwordsDiffer];
    setErrors(local);
    if (Object.keys(local).length > 0) return;

    ask(
      strings.confirm.setPassword(user.name),
      () =>
        save.mutateAsync({ id: user.id, newPassword: password }).then(
          () => {
            toast(`${strings.users.passwordSet} ${user.name}. ${strings.users.passwordSetDetail}`);
            onClose();
          },
          (error: Error) => {
            const fields =
              'fieldErrors' in error
                ? (error as { fieldErrors: Record<string, string[]> }).fieldErrors
                : {};
            setErrors(fields);
            if (fields.newPassword === undefined) toast(failureWords(error));
          },
        ),
      {
        title: `${strings.users.setPasswordTitle} ${user.name}`,
        confirmLabel: strings.users.setPassword,
      },
    );
  }

  return (
    <Modal
      title={`${strings.users.setPasswordTitle} ${user.name}`}
      lead={strings.users.setPasswordLead}
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose} disabled={save.isPending}>
            {strings.users.cancel}
          </Button>
          <Button variant="primary" disabled={save.isPending} onClick={submit}>
            {save.isPending ? strings.users.saving : strings.users.setPassword}
          </Button>
        </>
      }
    >
      <div className={modalStyles.field}>
        <label className={modalStyles.label} htmlFor={passwordId}>
          {strings.users.newPassword}
        </label>
        <PasswordInput
          id={passwordId}
          className={modalStyles.input}
          value={password}
          autoComplete="new-password"
          onChange={(event) => setPassword(event.target.value)}
        />
        <span className={styles.checkHint}>{strings.users.passwordHint}</span>
        {errors.newPassword !== undefined && (
          <p className={modalStyles.error}>{errors.newPassword.join(' ')}</p>
        )}
      </div>

      <div className={modalStyles.field}>
        <label className={modalStyles.label} htmlFor={repeatId}>
          {strings.users.repeatPassword}
        </label>
        <PasswordInput
          id={repeatId}
          className={modalStyles.input}
          value={repeat}
          autoComplete="new-password"
          onChange={(event) => setRepeat(event.target.value)}
        />
        {errors.repeat !== undefined && <p className={modalStyles.error}>{errors.repeat.join(' ')}</p>}
      </div>
    </Modal>
  );
}
